import type { ComponentProps } from 'react';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon, type FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

// IconName and IconType are imported from the centralized icons util
const iconContainer = cva(
  'inline-flex items-center justify-center overflow-hidden [&>svg]:pointer-events-none',
  {
    variants: {
      shape: {
        round: 'rounded-full',
        square: 'rounded-lg',
      },
      size: {
        sm: 'size-8 p-2',
        md: 'size-10 p-4',
        lg: 'size-12 p-6',
      },
      variant: {
        filled: '',
        outlined: 'bg-transparent border rounded-md',
      },
      colorScheme: {
        primary: 'text-primary',
        secondary: 'text-secondary-foreground',
        success: 'text-green-600',
        warning: 'text-amber-700',
        error: 'text-destructive',
      },
    },
    compoundVariants: [
      { variant: 'filled', colorScheme: 'primary', class: 'bg-primary/5' },
      { variant: 'filled', colorScheme: 'secondary', class: 'bg-secondary/5' },
      { variant: 'filled', colorScheme: 'success', class: 'bg-green-500/5' },
      { variant: 'filled', colorScheme: 'warning', class: 'bg-amber-500/5' },
      { variant: 'filled', colorScheme: 'error', class: 'bg-destructive/10' },
      { variant: 'outlined', colorScheme: 'primary', class: 'border-primary/40' },
      { variant: 'outlined', colorScheme: 'secondary', class: 'border-secondary' },
      { variant: 'outlined', colorScheme: 'success', class: 'border-green-500/40' },
      { variant: 'outlined', colorScheme: 'warning', class: 'border-amber-500/40' },
      { variant: 'outlined', colorScheme: 'error', class: 'border-destructive/40' },
    ],
    defaultVariants: {
      shape: 'round',
      size: 'md',
      variant: 'filled',
      colorScheme: 'primary',
    },
  },
);

type IconContainerVariants = VariantProps<typeof iconContainer>;

interface IconBaseProps
  extends Omit<ComponentProps<typeof FontAwesomeIcon>, 'icon' | 'size' | 'className'> {
  icon: IconDefinition;
}

type IconProps = IconBaseProps & IconContainerVariants & { className?: string };

// Reference: https://fontawesome.com/search?ip=classic&s=regular&ic=pro-collection&o=r
// Find classic regular icons here
function FeaturedIcon({ icon, shape, size, variant, colorScheme, className, ...props }: IconProps) {
  let svgSize: FontAwesomeIconProps['size'];

  if (size === 'sm') svgSize = 'xs';
  else if (size === 'lg') svgSize = 'lg';
  else svgSize = 'sm';

  return (
    <span className={cn(iconContainer({ shape, size, variant, colorScheme }), className)}>
      {icon ? <FontAwesomeIcon icon={icon} {...props} size={svgSize} /> : null}
    </span>
  );
}

interface SimpleIconProps extends Omit<ComponentProps<typeof FontAwesomeIcon>, 'icon'> {
  icon: IconDefinition;
}

function Icon({ icon, className, ...props }: SimpleIconProps) {
  if (!icon) return null;
  return <FontAwesomeIcon icon={icon} className={cn('shrink-0 size-4', className)} {...props} />;
}

export { FeaturedIcon, Icon };
